import React, { useState } from "react";
//* Importing components from react-bootstrap
import {
  Form,
  FormControl as Input,
  Row,
  Col,
} from "react-bootstrap";
import { Attention } from "../../../component/base/Attention";
//? Importing types
import { TestModel } from "./types/types";

type ExamFormFieldsProps = {
  model: TestModel;
  setModel: React.Dispatch<React.SetStateAction<TestModel>>;
  loadingInput: boolean;
  paramsId?: string;
  editMode?: boolean;
};

const ExamFormFields: React.FC<ExamFormFieldsProps> = ({
  model,
  setModel,
  loadingInput,
  editMode,
}) => {
  const isNameValid = model.name.length >= 3;

  // Variáveis aleatórias para os novos campos
  const [orderQuestions, setOrderQuestions] = useState("default");
  const [orderAlternatives, setOrderAlternatives] = useState("random");
  const [displayQuestions, setDisplayQuestions] = useState("all");
  const [canGoBack, setCanGoBack] = useState(true);
  const [limitExams, setLimitExams] = useState(true);
  const [examCount, setExamCount] = useState(1);
  const [examEndOption, setExamEndOption] = useState("download");

  return (
    <Form>
      {/* Nome e Descrição em linha única */}
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="exam-name">
              Nome da prova <Attention />
            </Form.Label>
            <Input
              id="exam-name"
              placeholder="Digite o nome da prova"
              value={model.name}
              onChange={(e) => setModel({ ...model, name: e.target.value })}
              required
              isInvalid={!isNameValid && model.name.length > 0}
            />
            {!isNameValid && model.name.length > 0 && (
              <Form.Text className="text-danger">
                O nome deve ter pelo menos 3 caracteres.
              </Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="exam-description">Descrição</Form.Label>
            <Input
              id="exam-description"
              placeholder="Digite uma descrição"
              value={model.description}
              onChange={(e) =>
                setModel({ ...model, description: e.target.value })
              }
              as="textarea"
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Tipo de Prova e novos campos abaixo */}
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="exam-type">
              Tipo de Prova <Attention />
            </Form.Label>
            <Form.Select
              id="exam-type"
              disabled={loadingInput || editMode}
              onChange={(e) =>
                setModel({
                  ...model,
                  type: e.target.value as "objective" | "dissertative",
                })
              }
              value={model.type}
            >
              <option value="objective">Prova Objetiva</option>
              <option value="dissertative">Prova Dissertativa</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Novos campos logo abaixo do Tipo de Prova */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="order-questions">Ordem das questões *</Form.Label>
            <div id="order-questions">
              <Form.Check
                type="radio"
                id="order-default"
                label="Na ordem de cadastro"
                name="orderQuestions"
                checked={orderQuestions === "default"}
                onChange={() => setOrderQuestions("default")}
              />
              <Form.Check
                type="radio"
                id="order-random"
                label="Ordem aleatória"
                name="orderQuestions"
                checked={orderQuestions === "random"}
                onChange={() => setOrderQuestions("random")}
              />
            </div>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="order-alternatives">Ordem das alternativas *</Form.Label>
            <div id="order-alternatives">
              <Form.Check
                type="radio"
                id="alt-default"
                label="Na ordem de cadastro"
                name="orderAlternatives"
                checked={orderAlternatives === "default"}
                onChange={() => setOrderAlternatives("default")}
              />
              <Form.Check
                type="radio"
                id="alt-random"
                label="Ordem aleatória"
                name="orderAlternatives"
                checked={orderAlternatives === "random"}
                onChange={() => setOrderAlternatives("random")}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="display-questions">Exibição das questões *</Form.Label>
            <div id="display-questions">
              <Form.Check
                type="radio"
                id="display-all"
                label="Todas exibidas de uma vez"
                name="displayQuestions"
                checked={displayQuestions === "all"}
                onChange={() => setDisplayQuestions("all")}
              />
              <Form.Check
                type="radio"
                id="display-one"
                label="Uma por vez"
                name="displayQuestions"
                checked={displayQuestions === "one"}
                onChange={() => setDisplayQuestions("one")}
              />
            </div>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="can-go-back">Permitir retorno a questões anteriores? *</Form.Label>
            <div id="can-go-back">
              <Form.Check
                type="radio"
                id="go-back-yes"
                label="Sim"
                name="canGoBack"
                checked={canGoBack}
                onChange={() => setCanGoBack(true)}
              />
              <Form.Check
                type="radio"
                id="go-back-no"
                label="Não"
                name="canGoBack"
                checked={!canGoBack}
                onChange={() => setCanGoBack(false)}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>

      {/* Configuração de número de provas */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="limit-exams">
              Definir número máximo de provas aleatórias? *
            </Form.Label>
            <div id="limit-exams">
              <Form.Check
                type="radio"
                id="limit-yes"
                label="Sim"
                name="limitExams"
                checked={limitExams}
                onChange={() => setLimitExams(true)}
              />
              <Form.Check
                type="radio"
                id="limit-no"
                label="Não"
                name="limitExams"
                checked={!limitExams}
                onChange={() => setLimitExams(false)}
              />
            </div>
          </Form.Group>

          {limitExams && (
            <Form.Group className="mb-3">
              <Form.Label htmlFor="exam-count">Número de provas geradas:</Form.Label>
              <Input
                id="exam-count"
                type="number"
                min="1"
                value={examCount}
                onChange={(e) => setExamCount(Number(e.target.value))}
              />
            </Form.Group>
          )}
        </Col>
      </Row>

      {/* Configuração final da prova */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="exam-end-options">O que exibir ao final da prova? *</Form.Label>
            <div id="exam-end-options">
              <Form.Check
                type="radio"
                id="end-download"
                label="Folha de resposta do candidato"
                name="examEndOption"
                checked={examEndOption === "download"}
                onChange={() => setExamEndOption("download")}
              />
              <Form.Check
                type="radio"
                id="end-results"
                label="Relação de acertos/erros"
                name="examEndOption"
                checked={examEndOption === "results"}
                onChange={() => setExamEndOption("results")}
              />
              <Form.Check
                type="radio"
                id="end-none"
                label="Nenhuma das opções"
                name="examEndOption"
                checked={examEndOption === "none"}
                onChange={() => setExamEndOption("none")}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

export default ExamFormFields;
